import { z } from 'zod';

import { SignupFieldEntrySchema } from './signup-custom-fields.schema';
import { isValidPhoneNumber } from '@kit/ui/phone-input';

import { FullNameSchema } from './full-name.schema';

/**
 * Phone number validation schema using the PhoneInput component validation
 */
export const PhoneSchema = z.string()
  .min(1, 'Phone number is required')
  .refine((value) => {
    // If no value, let the min validation handle it
    if (!value) return false;

    // Use the library's built-in validation which handles all countries
    // This works with the country dropdown and validates based on selected country
    return isValidPhoneNumber(value);
  }, {
    message: 'Please enter a valid phone number for the selected country'
  });

/**
 * Phone sign-in schema for sending OTP
 */
export const PhoneSignInSchema = z.object({
  phone: PhoneSchema,
});

/**
 * Phone sign-up schema for account creation.
 * Email is required — the Send-SMS hook fan-out delivers OTPs to both
 * WhatsApp and email, so an address on file is part of the auth contract.
 *
 * NOTE: this is the LEGACY single-screen schema. The phone-first flow now
 * collects only the phone before OTP (PhoneSignInSchema) and gathers name +
 * email AFTER verification (PhoneSignupProfileSchema). Kept for any callers
 * still relying on the combined shape.
 */
export const PhoneSignUpSchema = z.object({
    signupFields: z.array(SignupFieldEntrySchema).optional(),
  name: FullNameSchema,
  // Required to COLLECT; only verification is optional (the skippable email
  // verify step). The typo/junk-address concern that once made this optional
  // is now handled by the mismatch banners + verified-only sends.
  email: z
    .string()
    .trim()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  phone: PhoneSchema,
});

/**
 * Profile fields collected AFTER phone OTP verification in the phone-first
 * sign-up flow. The phone is already proven (it's the session identity), so it
 * is not part of this form.
 *
 * Email is REQUIRED to collect — only its VERIFICATION is optional (the
 * skippable SignupEmailVerifyStep). The typo'd/junk-address concern that once
 * made this optional is now handled by the email-mismatch banner and the
 * verified-only sending gate, so an unproven address can't leak anything.
 * The server action stays tolerant of absence (public endpoint).
 */
export const PhoneSignupProfileSchema = z.object({
    signupFields: z.array(SignupFieldEntrySchema).optional(),
  name: FullNameSchema,
  email: z
    .string()
    .trim()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
});

/**
 * Phone OTP verification schema
 */
export const PhoneOtpVerificationSchema = z.object({
  phone: PhoneSchema,
  token: z.string().min(6, 'Please enter a valid 6-digit code').max(6),
});

/**
 * Helper function to format phone number for display
 * Uses the formatPhoneNumber from react-phone-number-input for consistent formatting
 */
export { formatPhoneNumber } from '@kit/ui/phone-input';
